import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ApiError, useApi } from "../lib/api.ts";
import { useAsync } from "../lib/useAsync.ts";
import { formatRelative } from "../lib/format.ts";
import { skillHref, unitLabel, useSkillKey } from "../lib/skillKey.ts";
import { ErrorState, Spinner } from "../components/States.tsx";
import { primaryMarkdown } from "./SkillDetail.tsx";

const MARKDOWN = /\.(md|markdown)$/i;

export default function SkillEdit() {
  const api = useApi();
  const key = useSkillKey();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const requestedPath = search.get("path");

  const fetcher = useCallback(
    async (signal: AbortSignal) => {
      const bundle = await api.getSkill(key, undefined, signal);
      const file = requestedPath
        ? bundle.files.find((f) => f.path === requestedPath)
        : primaryMarkdown(bundle.files);
      if (!file) throw new Error("No markdown file in this bundle to edit.");
      if (!MARKDOWN.test(file.path)) {
        throw new Error("Only *.md files are editable here.");
      }
      const content = await api.getSkillFile(key, file.path, bundle.version, signal);
      return { bundle, file, content };
    },
    [api, key.scope, key.agent, key.name, key.project, requestedPath],
  );
  const { data, error, loading, reload } = useAsync(fetcher, [fetcher]);

  const [text, setText] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [stale, setStale] = useState<number | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Seed the editor once per loaded version; keep local edits across reloads
  // only when the user explicitly reloads after a stale warning.
  useEffect(() => {
    if (data) {
      setText(data.content);
      setDirty(false);
      setStale(null);
      setSaveError(null);
    }
  }, [data]);

  const base = data?.bundle.head ?? data?.bundle.version ?? 0;
  const next = base + 1;

  async function save() {
    if (!data || saving || !dirty) return;
    setSaving(true);
    setSaveError(null);
    try {
      await api.saveSkillFile(key, data.file.path, text, base);
      navigate(skillHref(key));
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        const body = e.body as { head?: number } | undefined;
        setStale(body?.head ?? null);
      } else if (e instanceof ApiError && e.status === 403) {
        setSaveError("Only org admins can publish org-scoped skills.");
      } else {
        setSaveError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setSaving(false);
    }
  }

  function reloadLatest() {
    // Drops the local edit in favour of the newest version.
    reload();
  }

  const fileName = data?.file.path.split("/").pop() ?? "SKILL.md";

  return (
    <section>
      <div className="page-header">
        <div>
          <p className="page-subtitle" style={{ margin: "0 0 0.45rem" }}>
            <Link to="/skills" className="link">
              Skills
            </Link>{" "}
            / {key.scope} / {key.agent} /{" "}
            <Link to={skillHref(key)} className="link">
              {unitLabel(key.name)}
            </Link>
          </p>
          <h1 className="page-title">Edit {fileName}</h1>
          {data && (
            <p className="page-subtitle">
              Saving writes v{next} on top of v{base}. Members pick it up on their
              next <code className="inline-code">stift pull --skills</code>.
            </p>
          )}
        </div>
      </div>

      {loading && <Spinner />}
      {error && <ErrorState error={error} onRetry={reload} />}

      {data && (
        <>
          <div className="plate-head">
            <span>
              {data.file.path} · editing from v{base}
            </span>
            <span>markdown</span>
          </div>
          <textarea
            className="editor"
            spellCheck={false}
            aria-label={`${data.file.path} source`}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setDirty(e.target.value !== data.content);
            }}
            disabled={saving}
          />
          <div className="editor-bar">
            <span className="editor-note">
              Only <code className="inline-code">*.md</code> files are editable
              here. Scripts and other files change via{" "}
              <code className="inline-code">stift push --skills</code>.
            </span>
            <div className="page-actions">
              <Link to={skillHref(key)} className="btn btn--ghost">
                Cancel
              </Link>
              <button
                type="button"
                className="btn btn--primary"
                onClick={save}
                disabled={saving || !dirty || stale !== null}
              >
                {saving ? "Saving…" : `Save as v${next}`}
              </button>
            </div>
          </div>

          {stale !== null && (
            <div className="warning-banner" style={{ marginTop: "1.5rem" }}>
              <strong>Someone published first.</strong> The server is now at{" "}
              v{stale ?? "?"}, but your edit is based on v{base}. Copy your
              changes, then{" "}
              <button type="button" className="btn btn--ghost btn--sm" onClick={reloadLatest}>
                reload v{stale ?? "latest"}
              </button>{" "}
              and re-apply them.
            </div>
          )}
          {saveError && (
            <div className="warning-banner" style={{ marginTop: "1.5rem" }}>
              <strong>Save failed.</strong> {saveError}
            </div>
          )}
          {stale === null && !saveError && (
            <div className="warning-banner" style={{ marginTop: "1.5rem" }}>
              <strong>Heads up.</strong> {data.bundle.author || "someone"} pushed v
              {data.bundle.version} from {data.bundle.host || "a machine"}{" "}
              {formatRelative(data.bundle.created)}. If anyone pushes again before
              you save, you&apos;ll be asked to reload and re-apply your edit.
            </div>
          )}
        </>
      )}
    </section>
  );
}
